import React, { useState, useEffect } from "react";
import { getToken } from "./tokenStorage";
import { useContext } from "react";
import { AuthCtx } from "./AuthProvider";

export default function SupportPanel() {
  const [isSupportMode, setIsSupportMode] = useState(false);
  const [tickets, setTickets] = useState([]);
  const [repliesByTicketId, setRepliesByTicketId] = useState({});
  const [loadingTickets, setLoadingTickets] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [successMessage, setSuccessMessage] = useState("");
  const [technicianName, setTechnicianName] = useState("");
  const [replyTexts, setReplyTexts] = useState({});
  const { user } = useContext(AuthCtx);

  const getAuthHeaders = () => {
    const token = getToken();
    return token ? { Authorization: `Bearer ${token}` } : {};
  };

  const [auditories, setAuditories] = useState([]);
  const [form, setForm] = useState({
    description: "",
    audience: "",
    equipment_type: "Проектор",
    sender: user?.fio || "",
  });
  const [email] = useState(user?.email || "");
  const role = user?.role;

  // 🔹 ЗАГРУЗКА ВСЕХ ОТВЕТОВ ДЛЯ СПИСКА ЗАЯВОК
  const loadAllReplies = async (ticketsData) => {
    const allReplies = {};

    for (const ticket of ticketsData || []) {
      const shortId = ticket.id;
      try {
        const res = await fetch(
          `${window.location.origin}/support/${shortId}/replies`,
          {
            headers: {
              ...getAuthHeaders(),
            },
          }
        );

        if (!res.ok) {
          console.error(
            `Не удалось загрузить ответы для заявки ${shortId}:`,
            res.status
          );
          continue;
        }

        const replies = await res.json();
        allReplies[shortId] = Array.isArray(replies) ? replies : [];
      } catch (err) {
        console.error(
          `Ошибка при загрузке ответов для заявки ${shortId}:`,
          err
        );
      }
    }

    setRepliesByTicketId(allReplies);
  };

  // 🔹 ЗАГРУЗКА САМИХ ЗАЯВОК
  const fetchTickets = async () => {
    if (!user) return;
  
    try {
      const endpoint =
        user.role === "admin"
          ? "/equipment/tickets"
          : "/equipment/my-tickets";
  
      const res = await fetch(`${window.location.origin}${endpoint}`, {
        headers: {
          ...getAuthHeaders(),
        },
      });
  
      if (!res.ok) {
        console.error("Нет доступа к", endpoint, res.status);
        setTickets([]);
        return;
      }
  
      const data = await res.json();
      setTickets(Array.isArray(data) ? data : []);

      if (user.role === "admin") {
        await loadAllReplies(data);
      }


    } catch (err) {
      console.error("Ошибка при загрузке заявок:", err);
      setTickets([]);
    } finally {
      setLoadingTickets(false);
    }
  };
  

  useEffect(() => {
    if (!user) return;
    fetchTickets();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user]);
  


  useEffect(() => {
    const fetchAuditories = async () => {
      try {
        const res = await fetch(`${window.location.origin}/api/auditories`);
        const data = await res.json();
        setAuditories(
          (data.auditories || []).map((a) => a.replace("Аудитория ", ""))
        );
      } catch (err) {
        console.error("Ошибка при загрузке аудиторий:", err);
      }
    };
    fetchAuditories();
  }, []);

  const handleChange = (e) => {
    setForm({ ...form, [e.target.name]: e.target.value });
  };

  const handleSubmit = async () => {
    if (!form.audience || !form.sender || !form.description) {
      alert("Пожалуйста, заполните все поля.");
      return;
    }
    form.email = email;
    if (form.audience && !form.audience.startsWith("Аудитория_")) {
      form.audience = `Аудитория_${form.audience}`;
    }

    setSubmitting(true);
    try {
      const res = await fetch(`${window.location.origin}/equipment/create`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(form),
      });
      if (!res.ok) {
        const data = await res.json().catch(() => ({}));
        throw new Error(data.detail || "Не удалось создать заявку.");
      }

      await fetchTickets();
      setForm({
        description: "",
        audience: "",
        equipment_type: "Проектор",
        sender: "",
      });

      setSuccessMessage("✅ Заявка успешно отправлена!");
      setTimeout(() => setSuccessMessage(""), 5000);
    } catch (err) {
      alert(err?.message || "Ошибка при отправке заявки.");
    } finally {
      setSubmitting(false);
    }
  };

  const handleReplyChange = (ticketId, text) => {
    setReplyTexts((prev) => ({ ...prev, [ticketId]: text }));
  };

  const equipmentIcons = {
    Проектор: "📽️",
    Компьютер: "💻",
    "Интерактивная доска": "🧊",
    "Документ-камера": "📷",
  };

  return (
    <>
      {role === "admin" && (
      <div className="flex justify-center mb-6">
        <div className="relative flex items-center bg-gray-200 border border-gray-300 rounded-full w-full max-w-xs sm:max-w-md h-[44px] shadow-inner px-1">
          <div
            className={`absolute top-1 bottom-1 left-1 bg-blue-600 rounded-full shadow-md transition-all duration-300 ease-in-out ${
              isSupportMode ? "translate-x-full" : ""
            }`}
            style={{ width: "calc(50% - 0.25rem)" }}
          ></div>

          <button
            onClick={() => setIsSupportMode(false)}
            className={`relative z-10 w-1/2 h-full text-sm font-semibold flex items-center justify-center gap-2 rounded-full transition-colors duration-300 ${
              !isSupportMode ? "text-white" : "text-blue-700"
            }`}
          >
            👤<span>Пользователь</span>
          </button>

          <button
            onClick={() => setIsSupportMode(true)}
            className={`relative z-10 w-1/2 h-full text-sm font-semibold flex items-center justify-center gap-2 rounded-full transition-colors duration-300 ${
              isSupportMode ? "text-white" : "text-blue-700"
            }`}
          >
            🛠️<span>Техподдержка</span>
          </button>
        </div>
      </div>
      )}

      {!isSupportMode ? (
        <>
          <div className="bg-white/30 backdrop-blur-md border border-white/20 p-4 sm:p-8 rounded-2xl shadow-xl">
            <h2 className="text-2xl font-bold mb-6">🔧 Заявка в техподдержку</h2>

            <div className="mb-4">
              <label className="block font-medium">Аудитория:</label>
              <input
                list="auditories"
                name="audience"
                value={form.audience}
                onChange={handleChange}
                className="w-full p-2 text-sm sm:text-base border rounded focus:ring-2 focus:ring-green-400"
                placeholder="Например: 201 или 318"
              />
              <datalist id="auditories">
                {auditories.map((a, idx) => (
                  <option key={idx} value={a} />
                ))}
              </datalist>
            </div>

            <div className="mb-4">
              <label className="block font-medium">Тип оборудования:</label>
              <select
                name="equipment_type"
                value={form.equipment_type}
                onChange={handleChange}
                className="w-full p-2 text-sm sm:text-base border rounded"
              >
                <option>Проектор</option>
                <option>Ноутбук</option>
                <option>Стационарный ПК</option>
                <option>Интерактивная доска</option>
                <option>Принтер</option>
                <option>Документ-камера</option>
                <option>Wi-Fi роутер</option>
                <option>Микрофон / аудио</option>
                <option>Кабель / переходник</option>
                <option>Другое</option>
              </select>
            </div>

            <div className="mb-4">
              <label className="block font-medium">Описание:</label>
              <textarea
                name="description"
                value={form.description}
                onChange={handleChange}
                className="w-full px-3 py-2 text-sm sm:text-base rounded-xl bg-white/40 backdrop-blur border border-white/20 shadow-inner placeholder-gray-500 text-gray-800 focus:outline-none focus:ring-2 focus:ring-green-400 transition min-h-[100px]"
                placeholder="Опишите проблему..."
              />
            </div>

            <div className="mb-4">
              <label className="block font-medium">Ваше имя:</label>
              <input
                name="sender"
                value={form.sender}
                readOnly
                className="w-full p-2 text-sm sm:text-base border rounded"
                placeholder="например, Иванов И.И."
              />
            </div>

            <div className="mb-4">
              <label className="block font-medium">
                Email (для уведомлений):
              </label>
              <input
                type="email"
                value={email}
                readOnly
                className="w-full p-2 text-sm sm:text-base border rounded"
                placeholder="example@email.com"
              />
            </div>

            <button
              onClick={handleSubmit}
              disabled={submitting}
              className="w-full sm:w-auto px-6 py-2.5 mt-2 bg-gradient-to-r from-green-500 to-green-600 text-white font-semibold rounded-xl shadow-md hover:from-green-600 hover:to-green-700 transition-all duration-300"
            >
              {submitting ? "Отправка..." : "Подать заявку"}
            </button>

            {successMessage && (
              <div className="mt-4 p-3 rounded bg-green-100 text-green-800 border border-green-300 text-sm">
                {successMessage}
              </div>
            )}
          </div>

          <div className="mt-10">
            <h3 className="text-xl font-bold mb-4">📋 Отправленные заявки</h3>
            {loadingTickets ? (
              <p className="text-gray-500">Загрузка заявок...</p>
            ) : tickets.length === 0 ? (
              <p className="text-gray-500">Заявок пока нет.</p>
            ) : (
              <div className="max-h-[400px] overflow-y-auto pr-1 sm:pr-2 grid grid-cols-1 sm:grid-cols-2 gap-4">
                {tickets.map((ticket, index) => (
                  <div
                    key={index}
                    className="p-4 sm:p-5 rounded-xl bg-white/40 backdrop-blur border border-white/20 shadow-sm text-sm sm:text-base"
                  >
                    <p>
                      <strong>Аудитория:</strong> {ticket.auditory}
                    </p>
                    <p className="flex items-center gap-2">
                      <strong>Тип:</strong>
                      <span className="inline-flex items-center gap-1 px-2 py-0.5 text-sm rounded-full bg-green-100 text-green-800">
                        {equipmentIcons[ticket.equipment] || "🔧"}{" "}
                        {ticket.equipment}
                      </span>
                    </p>
                    <p className="flex items-center gap-2">
                      <strong>Статус:</strong>
                      <span
                        className={`text-sm px-2 py-0.5 rounded-full font-medium ${
                          ticket.status === "Новая"
                            ? "bg-yellow-100 text-yellow-800"
                            : ticket.status === "В работе"
                            ? "bg-blue-100 text-blue-800"
                            : "bg-green-100 text-green-800"
                        }`}
                      >
                        {ticket.status || "Не указан"}
                      </span>
                    </p>
                    <p>
                      <strong>Описание:</strong> {ticket.description}
                    </p>
                    <p>
                      <strong>ФИО:</strong> {ticket.full_name || "—"}
                    </p>
                    <p>
                      <strong>Дата:</strong>{" "}
                      {new Date(ticket.date).toLocaleString()}
                    </p>

                    {repliesByTicketId[ticket.id] &&
                      repliesByTicketId[ticket.id].length > 0 && (
                        <div className="mt-3 bg-white/50 border border-gray-300 rounded-lg p-3 space-y-2">
                          <h4 className="font-semibold text-sm mb-2 text-gray-700">
                            📩 Ответы:
                          </h4>
                          {repliesByTicketId[ticket.id].map((reply, idx) => {
                            const isSupport =
                              reply.sender.toLowerCase().includes("тех") ||
                              reply.sender.toLowerCase().includes("support");
                            return (
                              <div
                                key={idx}
                                className="text-sm text-gray-800 flex gap-2 items-start"
                              >
                                <span className="text-xl">
                                  {isSupport ? "🛠️" : "👤"}
                                </span>
                                <div>
                                  <p className="font-semibold">
                                    {isSupport ? "Техподдержка" : reply.sender}
                                    <span className="ml-2 text-gray-400 text-xs">
                                      {new Date(
                                        reply.date
                                      ).toLocaleString()}
                                    </span>
                                  </p>
                                  <p className="text-gray-700">{reply.text}</p>
                                </div>
                              </div>
                            );
                          })}
                        </div>
                      )}
                  </div>
                ))}
              </div>
            )}
          </div>
        </>
      ) : (
        <div className="space-y-4">
          {tickets.map((ticket, index) => (
            <div
              key={index}
              className="p-3 sm:p-4 bg-white/50 rounded-xl border shadow-sm text-sm sm:text-base"
            >
              <div className="space-y-1 text-gray-800">
                <p>
                  <strong>🆔 ID:</strong> {ticket.id}
                </p>
                <p>
                  <strong>👤 ФИО:</strong> {ticket.full_name}
                </p>
                <p>
                  <strong>🏫 Аудитория:</strong> {ticket.auditory}
                </p>
                <p>
                  <strong>🖥️ Тип:</strong> {ticket.equipment}
                </p>
                <p>
                  <strong>📝 Описание:</strong> {ticket.description}
                </p>
                <p>
                  <strong>📅 Дата:</strong>{" "}
                  {new Date(ticket.date).toLocaleString()}
                </p>
                <p>
                  <strong>📦 Статус:</strong>
                  <span
                    className={`px-2 py-0.5 rounded-full font-semibold text-sm ${
                      ticket.status === "Новая"
                        ? "bg-yellow-100 text-yellow-800"
                        : ticket.status === "В_работе"
                        ? "bg-blue-100 text-blue-800"
                        : "bg-green-100 text-green-800"
                    }`}
                  >
                    {ticket.status}
                  </span>
                </p>
                {ticket.technician && (
                  <p>
                    <strong>👨‍🔧 Техник:</strong> {ticket.technician}
                  </p>
                )}
              </div>

              {ticket.status === "Новая" && (
                <button
                  onClick={async () => {
                    const name = prompt("Введите ваше имя:");
                    if (!name) return;
                    setTechnicianName(name);

                    const res = await fetch(
                      `${window.location.origin}/support/${ticket.id}/status`,
                      {
                        method: "PUT",
                        headers: {
                          "Content-Type": "application/json",
                          ...getAuthHeaders(),
                        },
                        body: JSON.stringify({ new_status: "В_работе" }),
                      }
                    );
                    if (!res.ok) {
                      const data = await res.json().catch(() => ({}));
                      alert(data.detail || "Не удалось обновить статус.");
                      return;
                    }
                    
                    

                    await fetchTickets();
                  }}
                  className="mt-3 w-full sm:w-auto px-4 py-2 bg-green-600 hover:bg-green-700 text-white rounded-md font-semibold transition"
                >
                  Взять в работу
                </button>
              )}

              {ticket.status === "В_работе" && (
                <div className="mt-3 flex flex-col gap-3">
                  <textarea
                    placeholder="Введите ответ..."
                    value={replyTexts[ticket.id] || ""}
                    onChange={(e) =>
                      handleReplyChange(ticket.id, e.target.value)
                    }
                    className="w-full p-2 rounded border border-gray-300 bg-white text-gray-900 text-sm sm:text-base min-h-[80px]"
                  />

                  <div className="flex flex-col sm:flex-row gap-2">
                    <button
                      onClick={async () => {
                        const res = await fetch(
                          `${window.location.origin}/support/${ticket.id}/status`,
                          {
                            method: "PUT",
                            headers: {
                              "Content-Type": "application/json",
                              ...getAuthHeaders(),
                            },
                            body: JSON.stringify({ new_status: "Завершена" }),
                          }
                        );
                        if (!res.ok) {
                          const data = await res.json().catch(() => ({}));
                          alert(data.detail || "Не удалось обновить статус.");
                          return;
                        }
                        
                        
                        await fetchTickets();
                      }}
                      className="w-full sm:w-auto px-4 py-2 bg-gray-700 hover:bg-gray-800 text-white rounded-md font-semibold transition"
                    >
                      Завершить
                    </button>

                    <button
                      onClick={async () => {
                        const reply = replyTexts[ticket.id];
                        if (!reply) return;

                        let sender = technicianName;
                        if (!sender) {
                          sender = prompt("Ваше имя (например: Техподдержка):");
                          if (!sender) return;
                          setTechnicianName(sender);
                          localStorage.setItem("technicianName", sender);
                        }

                        const res = await fetch(
                          `${window.location.origin}/support/${ticket.id}/reply`,
                          {
                            method: "POST",
                            headers: {
                              "Content-Type": "application/json",
                              ...getAuthHeaders(),
                            },
                            body: JSON.stringify({ text: reply, sender }),
                          }
                        );
                        if (!res.ok) {
                          const data = await res.json().catch(() => ({}));
                          alert(data.detail || "Не удалось отправить ответ.");
                          return;
                        }
                        
                        
                        await loadAllReplies(tickets);
                        setReplyTexts((prev) => ({
                          ...prev,
                          [ticket.id]: "",
                        }));
                        alert("Ответ отправлен!");
                      }}
                      className="w-full sm:w-auto px-4 py-2 bg-blue-500 hover:bg-blue-600 text-white rounded-md font-semibold transition"
                    >
                      Ответить
                    </button>
                  </div>
                </div>
              )}
            </div>
          ))}
        </div>
      )}
    </>
  );
}
